import { useEffect, useState } from "react";
import {
  WORKFLOW_LOG_DAYS,
  type WorkflowRunStatus,
  type WorkflowRunStepView,
  type WorkflowRunView,
} from "@dentacore/shared-types";

import { cancelWorkflowRun, workflowRuns } from "../api/workflows";
import { Modal } from "../ui/modal";
import styles from "./workflows.module.css";

/**
 * **A workflow's run log** (WG-D17): every run record by record — what each
 * step did, or why it stopped — kept 180 days. An administrator may cancel a
 * run still open (WG-D13).
 */
export function RunLog({
  workflowId,
  name,
  admin,
  onClose,
}: {
  workflowId: string;
  name: string;
  admin: boolean;
  onClose: () => void;
}): React.JSX.Element {
  const [runs, setRuns] = useState<WorkflowRunView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    workflowRuns(workflowId, controller.signal)
      .then(setRuns)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(cause instanceof Error ? cause.message : "Could not read the runs");
      });
    return () => controller.abort();
  }, [workflowId]);

  const cancel = (runId: string): void => {
    cancelWorkflowRun(runId)
      .then((run) => setRuns((all) => all?.map((r) => (r.id === run.id ? run : r)) ?? null))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "Could not cancel the run"),
      );
  };

  return (
    <Modal
      label={`Runs of ${name}`}
      onClose={onClose}
      testId="wf-runs-dialog"
      panelClassName={styles.logPanel}
    >
      <header className={styles.testHead}>
        <h2 className={styles.panelTitle}>Runs of “{name}”</h2>
        <span className={styles.sub}>Kept {WORKFLOW_LOG_DAYS} days</span>
        <button type="button" className={styles.x} aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {runs === null && error === null && <p className={styles.sub}>Reading the runs…</p>}
      {runs !== null && runs.length === 0 && <p className={styles.sub}>It has not run yet.</p>}
      {runs !== null && runs.length > 0 && (
        <ul className={styles.runList}>
          {runs.map((run) => (
            <li key={run.id} className={styles.run} data-testid="wf-run">
              <button
                type="button"
                className={styles.runHead}
                aria-expanded={open === run.id}
                onClick={() => setOpen(open === run.id ? null : run.id)}
              >
                <b>{run.recordName}</b>
                <StatusPill status={run.status} />
                <span className={styles.sub}>{run.outcome ?? ""}</span>
              </button>
              {open === run.id && (
                <div className={styles.runBody}>
                  {run.steps.length === 0 ? (
                    <p className={styles.sub}>No step has run yet.</p>
                  ) : (
                    <ol className={styles.steps}>
                      {run.steps.map((step, index) => (
                        <StepLine key={`${step.nodeId}-${String(index)}`} step={step} />
                      ))}
                    </ol>
                  )}
                  {admin && OPEN.includes(run.status) && (
                    <button
                      type="button"
                      className={styles.dangerLink}
                      onClick={() => cancel(run.id)}
                      data-testid="wf-run-cancel"
                    >
                      Cancel this run
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

const OPEN: readonly WorkflowRunStatus[] = [
  "RUNNING",
  "WAITING_PERSON",
  "WAITING_TASK",
  "WAITING_TIME",
  "WAITING_FIELD",
];

const STATUS_WORDS: Record<WorkflowRunStatus, string> = {
  RUNNING: "Running",
  WAITING_PERSON: "Waiting for someone",
  WAITING_TASK: "Waiting for a task",
  WAITING_TIME: "Waiting",
  WAITING_FIELD: "Waiting for a change",
  DONE: "Done",
  STOPPED: "Stopped",
  FAILED: "Failed",
  CANCELLED: "Cancelled",
};

export function StatusPill({ status }: { status: WorkflowRunStatus }): React.JSX.Element {
  const tone =
    status === "DONE"
      ? styles.pillOk
      : status === "FAILED"
        ? styles.pillBad
        : OPEN.includes(status)
          ? styles.pillWait
          : styles.pillStop;
  return <span className={`${styles.pill} ${tone ?? ""}`}>{STATUS_WORDS[status]}</span>;
}

const RESULT_WORDS: Record<WorkflowRunStepView["result"], string> = {
  DONE: "Done",
  SKIPPED: "Skipped",
  STOPPED: "Stopped",
  TASK: "Task created",
  WAITING: "Waiting",
  FAILED: "Failed",
  FORK: "Fork",
  ASKED: "Asked",
};

function StepLine({ step }: { step: WorkflowRunStepView }): React.JSX.Element {
  return (
    <li className={styles.step}>
      <span className={styles.stepLabel}>{step.label}</span>
      <span className={styles.pill}>{RESULT_WORDS[step.result]}</span>
      {step.message !== "" && <span className={styles.sub}>{step.message}</span>}
      <time className={styles.when} dateTime={step.at}>
        {new Date(step.at).toLocaleString(undefined, {
          day: "numeric",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        })}
      </time>
    </li>
  );
}
